/**
 * analyzeStale.ts
 *
 * Client-side stale marker for Analyze findings (Requirement 9.5).
 *
 * The backend has no marker for "the field behind this finding changed after
 * the run" (design.md, Backend gaps 4), so this device keeps two small records
 * per job in localStorage:
 *   - when each field was last confirmed or corrected (written by Extract mode)
 *   - when each run last finished, and the scope of the guideline run
 *
 * A finding with a field is stale when its field changed after the run that
 * produced it. Re-running replaces the run time, which clears the mark. Once
 * the backend stores the marker, drop this file and read the server value.
 *
 * Pure helpers take the records as arguments; the load/save helpers take an
 * optional storage so tests can pass a fake.
 */

import type { Finding, FindingKind } from './analyzeModel';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readJson(storage: StorageLike | null, key: string): unknown {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: StorageLike | null, key: string, value: unknown): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the marker stays off for this session.
  }
}

// ── Field changes ───────────────────────────────────────────────────────────

/** `${loanId}|${fieldId}` → epoch milliseconds of the last confirm or correction. */
export type FieldChanges = Record<string, number>;

export function fieldChangeKey(loanId: string, fieldId: string): string {
  return `${loanId}|${fieldId}`;
}

export function fieldChangesStorageKey(jobId: string): string {
  return `docmind:analyze-field-changes:${jobId}`;
}

export function loadFieldChanges(
  jobId: string,
  storage: StorageLike | null = defaultStorage()
): FieldChanges {
  const parsed = readJson(storage, fieldChangesStorageKey(jobId));
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const result: FieldChanges = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v)) result[k] = v;
  }
  return result;
}

/** Record that a field was confirmed, un-confirmed or corrected just now. */
export function recordFieldChange(
  jobId: string,
  loanId: string,
  fieldId: string,
  at: number = Date.now(),
  storage: StorageLike | null = defaultStorage()
): void {
  const changes = loadFieldChanges(jobId, storage);
  changes[fieldChangeKey(loanId, fieldId)] = at;
  writeJson(storage, fieldChangesStorageKey(jobId), changes);
}

// ── Run records ─────────────────────────────────────────────────────────────

export interface RunRecord {
  /** When completeness + consistency last finished (epoch ms). */
  checksAt?: number;
  /** When the guideline review last finished (epoch ms). */
  guidelineAt?: number;
  /** Loans the guideline run was limited to; `null` or absent means every loan. */
  guidelineScope?: string[] | null;
}

export function runRecordStorageKey(jobId: string, guidelineId: string): string {
  return `docmind:analyze-runs:${jobId}:${guidelineId}`;
}

export function loadRunRecord(
  jobId: string,
  guidelineId: string,
  storage: StorageLike | null = defaultStorage()
): RunRecord {
  const parsed = readJson(storage, runRecordStorageKey(jobId, guidelineId));
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const raw = parsed as Record<string, unknown>;
  const record: RunRecord = {};
  if (typeof raw.checksAt === 'number') record.checksAt = raw.checksAt;
  if (typeof raw.guidelineAt === 'number') record.guidelineAt = raw.guidelineAt;
  if (Array.isArray(raw.guidelineScope)) {
    record.guidelineScope = raw.guidelineScope.filter((x): x is string => typeof x === 'string');
  } else if (raw.guidelineScope === null) {
    record.guidelineScope = null;
  }
  return record;
}

export function saveRunRecord(
  jobId: string,
  guidelineId: string,
  patch: RunRecord,
  storage: StorageLike | null = defaultStorage()
): RunRecord {
  const next = { ...loadRunRecord(jobId, guidelineId, storage), ...patch };
  writeJson(storage, runRecordStorageKey(jobId, guidelineId), next);
  return next;
}

// ── Stale marking ───────────────────────────────────────────────────────────

/** The run that produced a kind of finding. */
export function runTimeFor(kind: FindingKind, record: RunRecord): number {
  // Unknown run time (results loaded from an earlier session) counts as 0, so
  // any change recorded on this device marks the finding stale.
  return (kind === 'guideline' ? record.guidelineAt : record.checksAt) ?? 0;
}

/** True when the finding's field changed after the run that produced it. */
export function isFindingStale(
  finding: Pick<Finding, 'kind' | 'fieldRef'>,
  changes: FieldChanges,
  record: RunRecord
): boolean {
  if (!finding.fieldRef) return false;
  const changedAt = changes[fieldChangeKey(finding.fieldRef.loanId, finding.fieldRef.fieldId)];
  return changedAt !== undefined && changedAt > runTimeFor(finding.kind, record);
}

export function markStale(
  findings: ReadonlyArray<Finding>,
  changes: FieldChanges,
  record: RunRecord
): Finding[] {
  return findings.map((f) => ({ ...f, stale: isFindingStale(f, changes, record) }));
}
